// Import-free geometry shared by the editor and generator. Coordinates are CDN-oriented.
/** @typedef {{x: number, y: number, w: number, h: number}} CropRect */
/** @typedef {{left: number, top: number, width: number, height: number}} PixelRect */
export const CROP_PRESETS = Object.freeze({
  free: Object.freeze({ratio: null, max: 2560}),
  background: Object.freeze({ratio: 16 / 9, max: 2560}),
  profile: Object.freeze({ratio: 1, max: 1024}),
  'banner-desktop': Object.freeze({ratio: 3, max: 2400}),
  'banner-mobile': Object.freeze({ratio: 2, max: 1600}),
  social: Object.freeze({ratio: 1200 / 630, width: 1200, height: 630}),
  'product-listing': Object.freeze({ratio: 4 / 3, max: 1600}),
});
export function cropError(status, code, data) {
  return Object.assign(new Error(code), {status, code, ...(data ? {data} : {})});
}
export function cropPreset(preset) {
  if (typeof preset !== 'string' || !Object.hasOwn(CROP_PRESETS, preset)) throw cropError(400, 'crop_preset_invalid');
  return CROP_PRESETS[preset];
}
export function normalizeRect(rect) {
  if (!rect || ['x', 'y', 'w', 'h'].some(key => !Number.isFinite(rect[key]))
    || rect.x < -1e-6 || rect.y < -1e-6 || rect.w <= 0 || rect.h <= 0
    || rect.x + rect.w > 1 + 1e-6 || rect.y + rect.h > 1 + 1e-6) {
    throw cropError(400, 'crop_rect_invalid');
  }
  const x = Math.max(0, rect.x), y = Math.max(0, rect.y);
  return {x, y, w: Math.min(rect.w, 1 - x), h: Math.min(rect.h, 1 - y)};
}
export function pixelRect(rect, width, height) {
  const r = normalizeRect(rect);
  const edge = (n, size) => Math.min(size, Math.max(0, Math.round(n * size)));
  const left = edge(r.x, width), top = edge(r.y, height);
  return {left, top, width: edge(r.x + r.w, width) - left,
    height: edge(r.y + r.h, height) - top};
}
export function cropGeometry(preset, rect, width, height) {
  const config = cropPreset(preset);
  const pixels = pixelRect(rect, width, height);
  if (Math.min(pixels.width, pixels.height) < 32) throw cropError(400, 'crop_too_small');
  if (config.ratio && Math.abs(pixels.width / pixels.height / config.ratio - 1) > 0.01) {
    throw cropError(400, 'crop_ratio_mismatch');
  }
  return pixels;
}
export function outputSize(preset, width, height) {
  const config = cropPreset(preset);
  if (config.width) return {width: config.width, height: config.height};
  const outWidth = config.ratio ? Math.min(width, config.max)
    : Math.round(width * Math.min(1, config.max / Math.max(width, height)));
  return {width: outWidth, height: config.ratio ? Math.round(outWidth / config.ratio)
    : Math.round(height * Math.min(1, config.max / Math.max(width, height)))};
}
